import React, { memo, useState } from 'react';
import styled from 'styled-components';
import {
  IconZoomInOutline,
  IconButton,
  IconReload,
  TextButton,
} from '@zillow/constellation';
import { convertRemToPx } from 'zdocs-web-core/lib/utils/dom-util';

import { SourcePage } from './types/compose-types';
import ZoomPageModal from './ZoomPageModal';
import { isSelectedPage } from './utils/page-range-utils';

const THUMBNAIL_WIDTH_REM = '13rem';
const THUMBNAIL_WIDTH = convertRemToPx(THUMBNAIL_WIDTH_REM);

const PagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  width: 100%;
  grid-gap: 1rem;
  background: transparent;
`;

const ThumbnailContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  padding-left: 1rem;
  padding-right: 1rem;
  align-items: center;
  border: none;
  background: transparent;
`;

const PageButton = styled.button<{ isLoaded: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${props => (props.isLoaded ? 1 : 0)};
  cursor: pointer;
  border: none;
  background: transparent;

  &:focus {
    outline: none;
  }
`;

type PageImageProps = {
  isActive: boolean;
  pageHeight: number;
  rotation: number;
};
const PageImage = styled.img<PageImageProps>`
  border: 2px solid
    ${props =>
      props.isActive ? props.theme.constellation.colors.brand : 'none'};
  box-shadow: ${props =>
    props.isActive ? 'none' : '0 2px 4px 0 rgba(0, 0, 0, 0.2)'};
  width: ${THUMBNAIL_WIDTH};
  height: ${props => props.pageHeight}px;
  transform: rotate(${props => props.rotation}deg);
`;

const PageControlSection = styled.section<{ isLoaded: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  width: 100%;
  opacity: ${props => (props.isLoaded ? 1 : 0)};
  border: none;
  background: transparent;
  padding-top: 0.5rem;
  padding-bottom: 2rem;

  &:focus {
    outline: none;
  }
`;

type ThumbnailProps = {
  documentName: string;
  page: SourcePage;
  selectedPage: boolean;
  onPageSelected: (page: SourcePage, shift: boolean) => void;
  onZoom: (page: SourcePage) => void;
  onPageRotation: (page: SourcePage) => void;
};

function Thumbnail({
  documentName,
  page,
  selectedPage,
  onPageSelected,
  onZoom,
  onPageRotation,
}: ThumbnailProps) {
  const pageNumber = page.pageImage.pageNumber.toString().padStart(2, '0');
  const [isLoaded, setIsLoaded] = useState(false);

  const handlePageSelected = function (
    e: React.MouseEvent<HTMLButtonElement>,
    page: SourcePage
  ) {
    onPageSelected(page, e.shiftKey);
  };

  const aspectRatio = page.height / page.width;
  const pageHeight = THUMBNAIL_WIDTH * aspectRatio;
  const isActive = selectedPage;
  const basePageImageUrl = `${page.pageImage.url}`;
  const url = `${basePageImageUrl}&width=${THUMBNAIL_WIDTH}`;
  const label = `${documentName} page ${pageNumber}`;

  return (
    <ThumbnailContainer>
      <PageButton
        aria-selected={isActive}
        aria-label={label}
        key={page.id}
        isLoaded={isLoaded}
        onClick={e => handlePageSelected(e, page)}
      >
        <PageImage
          isActive={selectedPage}
          src={url}
          alt={`Thumbnail for ${label}`}
          onLoad={() => setIsLoaded(true)}
          pageHeight={pageHeight}
          rotation={page.rotation}
        />
      </PageButton>
      <PageControlSection isLoaded={isLoaded}>
        <IconButton
          title={`Rotate Page ${pageNumber}`}
          onClick={() => onPageRotation(page)}
          size="sm"
          bare
          icon={<IconReload />}
        />
        <TextButton
          buttonType="secondary"
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
            handlePageSelected(e, page)
          }
        >
          {pageNumber}
        </TextButton>
        <IconButton
          title={`Zoom Page ${pageNumber}`}
          onClick={() => onZoom(page)}
          size="sm"
          bare
          icon={<IconZoomInOutline />}
        />
      </PageControlSection>
    </ThumbnailContainer>
  );
}

type PageSelectionPanelProps = {
  documentId: string;
  documentName: string;
  pages: Array<SourcePage>;
  onPageSelected: (page: SourcePage, shiftKey: boolean) => void;
  onPageRotation: (page: SourcePage) => void;
};
function DocumentThumbnailHorizontalViewer({
  documentId,
  documentName,
  pages,
  onPageSelected,
  onPageRotation,
}: PageSelectionPanelProps) {
  const [pageToZoom, setPageToZoom] = useState<SourcePage | null>(null);
  const closeZoom = function () {
    setPageToZoom(null);
  };

  return (
    <PagesContainer>
      {pages
        .filter(page => page.documentId === documentId)
        .map(p => (
          <Thumbnail
            key={p.id}
            documentName={documentName}
            page={p}
            selectedPage={isSelectedPage(pages, p.documentId, p.pageNumber)}
            onPageSelected={onPageSelected}
            onZoom={setPageToZoom}
            onPageRotation={onPageRotation}
          />
        ))}
      {pageToZoom && (
        <ZoomPageModal
          isOpen={!!pageToZoom}
          onClose={closeZoom}
          page={pageToZoom}
        />
      )}
    </PagesContainer>
  );
}

export default memo(DocumentThumbnailHorizontalViewer);
